// SEARCH-NOTIF in the in-memory API: ⌘K's `search.palette` and the caller's notification
// preferences (`notifications.prefs` / `setPrefs`), with the server's enforcement on the inbox.
//
// Parity by construction: the palette asks the twin's own list routes as the caller (people,
// digital employees, seats, teams, metrics, priorities, boards, docs, channels, tasks and
// messages), so each section is filtered by exactly the rule its screen uses here — a route that
// refuses the caller (403, a disabled add-on) contributes nothing, as the API's section does.
// Names match by prefix words (`textMatches`, M2-04's rule) or as a substring. Preferences use
// `@companyos/domain-work/chat`'s rules, the ones the API's consumer and digest job apply: the
// merged inbox drops kinds turned off and stamps `desktop` (preference and quiet hours).
//
// INBOX-PREFS: preferences are a preset (null: the company's default) plus row edits per
// notification type, a pause, and per-item overrides; the merged inbox drops a type whose inbox
// is off (never a waiting item) and items an override mutes.
import {
  type ChannelsPatch,
  categoryOf,
  DEFAULT_NUDGE_HOURS,
  DEFAULT_PREFS,
  EMAIL_AVAILABLE,
  effectivePreset,
  hhmmOf,
  isPreset,
  isTimeZone,
  itemRoute,
  minutesOf,
  NOTIFY_CATEGORIES,
  type NotifyCategory,
  type NotifyPreset,
  type OverrideLevel,
  overrideAllows,
  overrideKey,
  pauseUntil,
  presetRoutes,
  type RoutePatch,
  resolvedRoutes,
  resolvedTypes,
  routesFromKinds,
  type StoredPrefs,
  wantsAlert,
} from "@companyos/domain-work/chat";
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import { type Ctx, json, problem, validation } from "./fake";
import type { ComposedHandler } from "./fake-merge";
import { textMatches } from "./fake-merge";

type Hit = OperationOutput<"search.palette">["data"][number];
type Kind = Hit["kind"];
type PrefsPut = OperationInput<"notifications.setPrefs">["body"];

/** Asks the twin itself, as the signed-in caller (not logged as a screen call). */
export type InternalFetch = (
  path: string,
  init?: { method?: string; body?: unknown },
) => Promise<Response>;

const ORDER: Kind[] = [
  "person",
  "agent",
  "seat",
  "team",
  "metric",
  "priority",
  "board",
  "doc",
  "channel",
  "task",
  "message",
  "file",
];

const blank = (kind: Kind, id: string, title: string, subtitle: string | null): Hit => ({
  kind,
  id,
  title,
  subtitle,
  recent: false,
  seat_id: null,
  board_id: null,
  task_number: null,
  channel_id: null,
  topic_id: null,
});

const matches = (q: string, text: string) =>
  q === "" ? false : textMatches(q, text) || text.toLowerCase().includes(q.toLowerCase());

async function rowsOf<T>(res: Response): Promise<T[]> {
  if (!res.ok) return [];
  const body = (await res.json()) as { data?: T[] };
  return body.data ?? [];
}

/** What the twin stores per person (the company's preset is read from its settings). */
type Stored = Omit<StoredPrefs, "companyPreset"> & { updatedAt: string; lastDigest: string | null };

/** The company the caller is in, as far as preferences go. */
type CompanyLike = {
  id: string;
  settings?: {
    notifications?:
      | { default_preset?: string | undefined; nudge_hours?: number | undefined }
      | undefined;
  } | null;
};

const companyPresetOf = (c: CompanyLike | undefined): NotifyPreset => {
  const v = c?.settings?.notifications?.default_preset;
  return isPreset(v) ? v : DEFAULT_PREFS.companyPreset;
};
const nudgeHoursOf = (c: CompanyLike | undefined): number => {
  const v = c?.settings?.notifications?.nudge_hours;
  return typeof v === "number" ? v : DEFAULT_NUDGE_HOURS;
};

export function createPaletteFake(deps: { internal: InternalFetch; now: () => Date }) {
  /** Stored preferences per company and membership (absent: defaults). */
  const prefs = new Map<string, Stored>();
  /** DE-REPLY-STYLE: each person's chosen reply length. */
  const replyStyles = new Map<
    string,
    { length: string; updated_at: string | null; quick_replies: boolean; show_costs: boolean }
  >();
  const key = (co: string, m: string) => `${co}:${m}`;
  const withCompany = (p: Stored | undefined, c: CompanyLike | undefined): StoredPrefs => ({
    ...DEFAULT_PREFS,
    ...(p ?? {}),
    companyPreset: companyPresetOf(c),
  });

  /** The caller's person membership in the company (null: no inbox, no preferences). */
  const meIn = (ctx: Ctx): string | null =>
    ctx.me.memberships.find(
      (m) => m.company_id === ctx.principal?.company.id && m.kind === "person",
    )?.membership_id ?? null;

  /** Every candidate of the company the caller may see, by kind (its list routes). */
  async function candidates(co: string, q: string): Promise<Map<Kind, Hit[]>> {
    const base = `/v1/companies/${co}`;
    const out = new Map<Kind, Hit[]>();
    const people = await rowsOf<{ id: string; display_name: string; kind: string; status: string }>(
      await deps.internal(`${base}/people?limit=200`),
    );
    out.set(
      "person",
      people
        .filter((p) => p.kind === "person" && p.status === "active")
        .map((p) => blank("person", p.id, p.display_name, "Person")),
    );
    const des = await rowsOf<{
      membership_id: string;
      display_name: string;
      status: string;
      seat: { id: string; title: string } | null;
    }>(await deps.internal(`${base}/digital-employees?limit=200`));
    out.set(
      "agent",
      des
        .filter((d) => d.status !== "retired")
        .map((d) => ({
          ...blank(
            "agent",
            d.membership_id,
            d.display_name,
            d.seat ? `Digital employee · ${d.seat.title}` : "Digital employee",
          ),
          seat_id: d.seat?.id ?? null,
        })),
    );
    const seats = await rowsOf<{ id: string; title: string; status: string }>(
      await deps.internal(`${base}/seats?limit=200`),
    );
    out.set(
      "seat",
      seats
        .filter((s) => s.status !== "proposed" && s.status !== "closed")
        .map((s) => ({ ...blank("seat", s.id, s.title, "Seat"), seat_id: s.id })),
    );
    const teams = await rowsOf<{ id: string; name: string; lead_seat_id: string | null }>(
      await deps.internal(`${base}/teams?limit=200`),
    );
    out.set(
      "team",
      teams.map((t) => ({ ...blank("team", t.id, t.name, "Team"), seat_id: t.lead_seat_id })),
    );
    const metrics = await rowsOf<{
      id: string;
      name: string;
      key: string;
      cadence: string | null;
      unit: string;
      archived_at: string | null;
    }>(await deps.internal(`${base}/metrics?limit=200`));
    out.set(
      "metric",
      metrics
        .filter((m) => !m.archived_at)
        .map((m) => ({
          ...blank("metric", m.id, m.name, `Metric · ${m.cadence ?? "any cadence"} · ${m.unit}`),
          // The key matches too (the API's `md.key ILIKE`).
          _key: m.key,
        })) as Hit[],
    );
    const pr = await deps.internal(`${base}/priorities`);
    const board = pr.ok
      ? ((await pr.json()) as {
          quarter: string;
          data: { id: string; title: string; seat_id: string }[];
        })
      : null;
    out.set(
      "priority",
      (board?.data ?? []).map((g) => ({
        ...blank("priority", g.id, g.title, `Priority · ${board?.quarter ?? ""}`),
        seat_id: g.seat_id,
      })),
    );
    const boards = await rowsOf<{ id: string; name: string; archived_at: string | null }>(
      await deps.internal(`${base}/boards?limit=200`),
    );
    out.set(
      "board",
      boards
        .filter((b) => !b.archived_at)
        .map((b) => ({ ...blank("board", b.id, b.name, "Board"), board_id: b.id })),
    );
    // TASK-FILES: files by name, board by board through `files.list` (the caller's view).
    const files = q
      ? (
          await Promise.all(
            boards
              .filter((b) => !b.archived_at)
              .map(async (b) =>
                rowsOf<{
                  id: string;
                  file_name: string;
                  task: { title: string; board_id: string; number: number } | null;
                }>(
                  await deps.internal(
                    `${base}/files?board_id=${b.id}&q=${encodeURIComponent(q)}&limit=20`,
                  ),
                ),
              ),
          )
        ).flat()
      : [];
    out.set(
      "file",
      files.map((f) => ({
        ...blank(
          "file",
          f.id,
          f.file_name,
          f.task ? `File · #${f.task.number} ${f.task.title}` : "File",
        ),
        board_id: f.task?.board_id ?? null,
        task_number: f.task?.number ?? null,
      })),
    );
    const docs = q
      ? await rowsOf<{ id: string; title: string; space_name?: string | null }>(
          await deps.internal(`${base}/pages/search`, {
            method: "POST",
            body: { query: q, limit: 50 },
          }),
        )
      : [];
    out.set(
      "doc",
      docs.map((d) => blank("doc", d.id, d.title, d.space_name ? `Doc · ${d.space_name}` : "Doc")),
    );
    const channels = await rowsOf<{
      id: string;
      name: string;
      kind: string;
      archived_at: string | null;
    }>(await deps.internal(`${base}/channels?limit=200`));
    out.set(
      "channel",
      channels
        .filter((c) => !c.archived_at && c.kind !== "dm" && c.kind !== "group_dm")
        .map((c) => ({ ...blank("channel", c.id, `#${c.name}`, "Channel"), channel_id: c.id })),
    );
    const found = q
      ? await rowsOf<{
          kind: "task" | "message";
          id: string;
          title: string;
          snippet: string;
          board_id: string | null;
          task_number: number | null;
          channel_id: string | null;
          topic_id: string | null;
        }>(await deps.internal(`${base}/search?q=${encodeURIComponent(q)}&limit=50`))
      : [];
    out.set(
      "task",
      found
        .filter((h) => h.kind === "task")
        .map((h) => ({
          ...blank("task", h.id, h.title, `Task #${h.task_number}`),
          board_id: h.board_id,
          task_number: h.task_number,
        })),
    );
    out.set(
      "message",
      found
        .filter((h) => h.kind === "message")
        .map((h) => ({
          ...blank("message", h.id, h.snippet, h.title),
          channel_id: h.channel_id,
          topic_id: h.topic_id,
        })),
    );
    return out;
  }

  const clean = (h: Hit): Hit => {
    const { _key: _drop, ...rest } = h as Hit & { _key?: string };
    return rest;
  };
  const hitMatches = (q: string, h: Hit) =>
    h.kind === "doc" || h.kind === "task" || h.kind === "message" || h.kind === "file"
      ? true // already matched by their own search routes
      : matches(q, h.title) || matches(q, (h as Hit & { _key?: string })._key ?? "");

  /** As the API: "#12 Title" for a task the caller can open, a DE's name; null when gone. */
  async function named(co: string, v: ReturnType<typeof viewOf>) {
    if (v.overrides.length === 0) return v;
    const base = `/v1/companies/${co}`;
    const des = v.overrides.some((o) => o.subject_kind === "agent")
      ? await rowsOf<{ membership_id: string; display_name: string }>(
          await deps.internal(`${base}/digital-employees?limit=200`),
        )
      : [];
    const overrides = [];
    for (const o of v.overrides) {
      let subject_name: string | null = null;
      if (o.subject_kind === "agent")
        subject_name = des.find((d) => d.membership_id === o.subject_id)?.display_name ?? null;
      else {
        const r = await deps.internal(`${base}/tasks/${o.subject_id}`);
        if (r.ok) {
          const t = (await r.json()) as { number: number; title: string };
          subject_name = `#${t.number} ${t.title}`;
        }
      }
      overrides.push({ ...o, subject_name });
    }
    return { ...v, overrides };
  }

  function viewOf(stored: Stored | undefined, c: CompanyLike | undefined) {
    const p = stored;
    const full = withCompany(stored, c);
    const routes = resolvedRoutes(full);
    const effective = effectivePreset(full);
    const paused = full.pausedUntil && full.pausedUntil > deps.now() ? full.pausedUntil : null;
    return {
      types: resolvedTypes(full),
      preset: full.preset,
      effective_preset: effective,
      company_preset: full.companyPreset,
      routes,
      customized: JSON.stringify(routes) !== JSON.stringify(presetRoutes(effective)),
      email_available: EMAIL_AVAILABLE,
      paused_until: paused ? paused.toISOString() : null,
      nudge_hours: nudgeHoursOf(c),
      overrides: Object.entries(full.overrides).flatMap(([k, level]) => {
        const [kind, id] = k.split(":");
        return level && (kind === "task" || kind === "agent") && id
          ? [
              {
                subject_kind: kind as "task" | "agent",
                subject_id: id,
                level,
                subject_name: null as string | null,
              },
            ]
          : [];
      }),
      quiet_hours:
        p && p.quietStart !== null && p.quietEnd !== null
          ? { start: hhmmOf(p.quietStart), end: hhmmOf(p.quietEnd), time_zone: p.timeZone ?? "UTC" }
          : null,
      digest_frequency: p?.digestFrequency ?? DEFAULT_PREFS.digestFrequency,
      last_digest_at: p?.lastDigest ?? null,
      updated_at: p?.updatedAt ?? null,
    };
  }

  const handlers: Record<string, ComposedHandler> = {
    "search.palette": async (ctx) => {
      const p = ctx.principal;
      if (!p) return problem(404, "not_found", "Not found");
      const q = (ctx.url.searchParams.get("q") ?? "").trim();
      const perKind = Math.min(
        20,
        Math.max(1, Number(ctx.url.searchParams.get("per_kind") ?? 5) || 5),
      );
      const recentRaw = ctx.url.searchParams.get("recent") ?? "";
      if (recentRaw && !/^([a-z]+:[0-9a-f-]{36})(,[a-z]+:[0-9a-f-]{36}){0,19}$/.test(recentRaw))
        return validation("recent: kind:id pairs, at most 20");
      const recent = recentRaw
        ? recentRaw.split(",").map((r) => {
            const [kind, id] = r.split(":");
            return { kind: kind as Kind, id: id as string };
          })
        : [];
      if (!q && recent.length === 0) return json({ data: [], took_ms: 0 });
      const all = await candidates(p.company.id, q);
      const out: Hit[] = [];
      const seen = new Set<string>();
      const base = `/v1/companies/${p.company.id}`;
      for (const r of recent) {
        if (r.kind === "message") continue;
        let h = (all.get(r.kind) ?? []).find((x) => x.id === r.id);
        // Docs and tasks are listed only by a query: a recent one is read by id.
        if (!h && (r.kind === "doc" || r.kind === "task")) {
          const res = await deps.internal(
            `${base}/${r.kind === "doc" ? "pages" : "tasks"}/${r.id}`,
          );
          if (res.ok) {
            const row = (await res.json()) as {
              id: string;
              title: string;
              board_id?: string;
              number?: number;
            };
            h =
              r.kind === "doc"
                ? blank("doc", row.id, row.title, "Doc")
                : {
                    ...blank("task", row.id, row.title, `Task #${row.number}`),
                    board_id: row.board_id ?? null,
                    task_number: row.number ?? null,
                  };
          }
        }
        if (!h || (q && !hitMatches(q, h)) || seen.has(`${h.kind}:${h.id}`)) continue;
        // A recent doc or task with a query: it must match by name here.
        if (q && (h.kind === "doc" || h.kind === "task") && !matches(q, h.title)) continue;
        seen.add(`${h.kind}:${h.id}`);
        out.push({ ...clean(h), recent: true });
      }
      if (q)
        for (const kind of ORDER) {
          const rows = (all.get(kind) ?? [])
            .filter((h) => hitMatches(q, h) && !seen.has(`${h.kind}:${h.id}`))
            .sort(
              (a, b) =>
                Number(b.title.toLowerCase().startsWith(q.toLowerCase())) -
                  Number(a.title.toLowerCase().startsWith(q.toLowerCase())) ||
                a.title.length - b.title.length ||
                a.title.localeCompare(b.title),
            )
            .slice(0, perKind);
          for (const h of rows) {
            seen.add(`${h.kind}:${h.id}`);
            out.push(clean(h));
          }
        }
      return json({ data: out, took_ms: 0 });
    },

    "notifications.prefs": async (ctx) => {
      const p = ctx.principal;
      if (!p) return problem(404, "not_found", "Not found");
      const me = meIn(ctx);
      return json(
        await named(
          p.company.id,
          viewOf(me ? prefs.get(key(p.company.id, me)) : undefined, p.company),
        ),
      );
    },

    "notifications.setPrefs": async (ctx) => {
      const p = ctx.principal;
      if (!p) return problem(404, "not_found", "Not found");
      const me = meIn(ctx);
      if (!me) return problem(404, "not_found", "Not found");
      const body = (ctx.body ?? {}) as PrefsPut;
      if (body.quiet_hours && !isTimeZone(body.quiet_hours.time_zone))
        return validation("quiet_hours.time_zone: not a known IANA time zone");
      if (body.pause?.time_zone && !isTimeZone(body.pause.time_zone))
        return validation("pause.time_zone: not a known IANA time zone");
      if (body.preset !== undefined && body.preset !== null && !isPreset(body.preset))
        return validation("preset: everything, needs_me, manager or quiet");
      const k = key(p.company.id, me);
      const before: Stored = prefs.get(k) ?? {
        ...DEFAULT_PREFS,
        updatedAt: "",
        lastDigest: null,
      };
      // A preset choice clears the row edits; `routes` (and the old per-kind `types`) apply over.
      const routes: Partial<Record<string, RoutePatch>> =
        body.preset !== undefined ? {} : { ...before.routes };
      const patches: Partial<Record<NotifyCategory, RoutePatch>>[] = [
        routesFromKinds((body.types ?? {}) as Partial<Record<string, ChannelsPatch>>),
        (body.routes ?? {}) as Partial<Record<NotifyCategory, RoutePatch>>,
      ];
      for (const patch of patches)
        for (const c of NOTIFY_CATEGORIES) {
          const r = patch[c];
          if (!r) continue;
          const merged: RoutePatch = { ...routes[c] };
          for (const [d, v] of Object.entries(r)) if (v !== undefined) merged[d as "inbox"] = v;
          routes[c] = merged;
        }
      const overrides = { ...before.overrides };
      for (const o of body.overrides ?? []) {
        const ok = overrideKey(o.subject_kind, o.subject_id);
        if (o.level === null) delete overrides[ok];
        else overrides[ok] = o.level as OverrideLevel;
      }
      const zone = body.pause?.time_zone ?? body.quiet_hours?.time_zone ?? before.timeZone ?? null;
      const next: Stored = {
        preset: body.preset !== undefined ? body.preset : before.preset,
        routes,
        overrides,
        pausedUntil:
          body.pause === undefined
            ? before.pausedUntil
            : body.pause === null
              ? null
              : pauseUntil(body.pause.for, deps.now(), zone),
        quietStart:
          body.quiet_hours === undefined
            ? before.quietStart
            : body.quiet_hours
              ? minutesOf(body.quiet_hours.start)
              : null,
        quietEnd:
          body.quiet_hours === undefined
            ? before.quietEnd
            : body.quiet_hours
              ? minutesOf(body.quiet_hours.end)
              : null,
        timeZone:
          body.quiet_hours === undefined ? before.timeZone : (body.quiet_hours?.time_zone ?? null),
        digestFrequency: body.digest_frequency ?? before.digestFrequency,
        updatedAt: deps.now().toISOString(),
        lastDigest: before.lastDigest,
      };
      const { updatedAt: _a, lastDigest: _b, ...b } = before;
      const { updatedAt: _c, lastDigest: _d, ...n } = next;
      if (JSON.stringify(b) === JSON.stringify(n))
        return json(await named(p.company.id, viewOf(prefs.get(k), p.company)));
      prefs.set(k, next);
      return json(await named(p.company.id, viewOf(next, p.company)));
    },

    // DE-REPLY-STYLE: a person's reply length (no choice: short).
    "replyStyle.get": async (ctx) => {
      const p = ctx.principal;
      const me = meIn(ctx);
      if (!p || !me) return problem(404, "not_found", "Not found");
      return json(
        replyStyles.get(key(p.company.id, me)) ?? {
          length: "short",
          updated_at: null,
          quick_replies: true,
          show_costs: true,
        },
      );
    },

    "replyStyle.set": async (ctx) => {
      const p = ctx.principal;
      const me = meIn(ctx);
      if (!p || !me) return problem(404, "not_found", "Not found");
      const body = (ctx.body ?? {}) as {
        length?: unknown;
        quick_replies?: unknown;
        show_costs?: unknown;
      };
      const { length, quick_replies, show_costs } = body;
      if (length === undefined && quick_replies === undefined && show_costs === undefined)
        return validation("send length, quick_replies or show_costs");
      if (show_costs !== undefined && typeof show_costs !== "boolean")
        return validation("show_costs: true or false");
      if (
        length !== undefined &&
        length !== "short" &&
        length !== "normal" &&
        length !== "detailed"
      )
        return validation("length: short, normal or detailed");
      if (quick_replies !== undefined && typeof quick_replies !== "boolean")
        return validation("quick_replies: true or false");
      const was = replyStyles.get(key(p.company.id, me)) ?? {
        length: "short",
        updated_at: null,
        quick_replies: true,
        show_costs: true,
      };
      const v = {
        show_costs: (show_costs as boolean | undefined) ?? was.show_costs,
        length: (length as string | undefined) ?? was.length,
        updated_at: length !== undefined ? deps.now().toISOString() : was.updated_at,
        quick_replies: (quick_replies as boolean | undefined) ?? was.quick_replies,
      };
      replyStyles.set(key(p.company.id, me), v);
      return json(v);
    },

    // W1-FIXES: an agent records a person's length from their message; the twin's callers are
    // people, who choose their own (my/reply-style), as the API answers them.
    "replyStyle.setFromMessage": async (ctx) => {
      if (!ctx.principal) return problem(404, "not_found", "Not found");
      return problem(403, "forbidden", "a person chooses their own reply length (my/reply-style)");
    },
  };

  /**
   * The API's projection rules on the merged inbox: a type whose inbox is off is not there (never
   * a waiting item), nor what a per-item override mutes; an item asks for a desktop notification
   * by its row, quiet hours and the pause at its arrival.
   */
  function stampInbox<
    T extends {
      kind: string;
      created_at: string;
      desktop: boolean;
      subject_kind?: string;
      subject_id?: string;
      category?: string | null;
      from_membership_id?: string | null;
    },
  >(company: CompanyLike, membershipId: string | null, items: T[]): T[] {
    const stored = membershipId ? prefs.get(key(company.id, membershipId)) : undefined;
    const p = withCompany(stored, company);
    return items.flatMap((i) => {
      const category = (i.category ??
        categoryOf(i.kind, i.subject_kind ?? null)) as NotifyCategory | null;
      if (!itemRoute(p, category).inbox) return [];
      const levels = [
        i.subject_kind === "task" && i.subject_id
          ? p.overrides[overrideKey("task", i.subject_id)]
          : undefined,
        i.from_membership_id ? p.overrides[overrideKey("agent", i.from_membership_id)] : undefined,
      ];
      if (levels.some((l) => !overrideAllows(l, i.kind, category))) return [];
      return [{ ...i, desktop: wantsAlert(p, category, new Date(i.created_at)) }];
    });
  }

  return { handlers, stampInbox, meIn };
}
